// src/pages/OrganizationMembersPage.jsx

import React, { useEffect, useState } from "react";
import { FRONT_URL } from "../config/apiConfig";
import { useSelector } from "react-redux";
import {
  getOrgInfo,
  removeUserFromOrg,
  updateUserRoleInOrg
} from "../services/organizationService";
import {
  createInvitation,
  getInvitationsForOrg,
  cancelInvitation
} from "../services/invitationService";
import {
  getJoinRequestsForOrganization,
  approveJoinRequest,
  rejectJoinRequest
} from "../services/joinRequestService";
import {
  Container,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  Select,
  MenuItem,
  TextField,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  IconButton,
  Avatar
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useSnackbar } from "notistack";

function OrganizationMembersPage() {
  const { user } = useSelector((state) => state.user);
  const { enqueueSnackbar } = useSnackbar();

  const [org, setOrg] = useState(null);
  const [error, setError] = useState("");

  // invite form
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("user");

  // open invitations
  const [invitations, setInvitations] = useState([]);

  // join requests
  const [joinRequests, setJoinRequests] = useState([]);

  // remove user dialog
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [removeUserId, setRemoveUserId] = useState(null);
  const [removeUserName, setRemoveUserName] = useState("");

  // cancel invitation dialog
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelInvId, setCancelInvId] = useState(null);
  const [cancelInvEmail, setCancelInvEmail] = useState("");

  useEffect(() => {
    fetchOrganization();
    // eslint-disable-next-line
  }, []);

  function showSuccess(msg) {
    enqueueSnackbar(msg, { variant: "success" });
  }
  function showError(msg) {
    enqueueSnackbar(msg, { variant: "error" });
  }

  async function fetchOrganization() {
    try {
      setError("");
      if (!user || !user.organization || !user.organization._id) {
        setError("No organization found");
        return;
      }
      const orgId = user.organization._id;
      const data = await getOrgInfo(orgId);
      setOrg(data);
      fetchInvitations(orgId);
      fetchJoinRequests(orgId);
    } catch (err) {
      setError("Failed to load organization");
      showError("Failed to load organization");
    }
  }

  async function fetchInvitations(orgId) {
    try {
      const invs = await getInvitationsForOrg(orgId);
      setInvitations(invs);
    } catch (err) {
      showError("Failed to load invitations");
    }
  }

  async function fetchJoinRequests(orgId) {
    try {
      const requests = await getJoinRequestsForOrganization(orgId);
      setJoinRequests(requests);
    } catch (err) {
      showError("Failed to load join requests");
    }
  }

  async function handleRemoveConfirmed() {
    if (!org || !org._id || !removeUserId) return;
    try {
      await removeUserFromOrg(org._id, removeUserId);
      showSuccess("User removed");
      fetchOrganization();
    } catch (err) {
      showError("Failed to remove user");
    }
    closeRemoveDialog();
  }

  function openRemoveDialog(memberId, memberName) {
    setRemoveUserId(memberId);
    setRemoveUserName(memberName);
    setRemoveDialogOpen(true);
  }

  function closeRemoveDialog() {
    setRemoveDialogOpen(false);
    setRemoveUserId(null);
    setRemoveUserName("");
  }

  async function handleRoleChange(memberId, newRole) {
    if (!org || !org._id) return;
    try {
      await updateUserRoleInOrg(org._id, memberId, newRole);
      showSuccess("Role updated");
      fetchOrganization();
    } catch (err) {
      showError("Failed to update role");
    }
  }

  async function handleInvite(e) {
    e.preventDefault();
    if (!org || !org._id) return;
    try {
      const data = await createInvitation(org._id, inviteEmail, inviteRole);
      showSuccess(data.message);
      if (data.link) {
        enqueueSnackbar(`Invitation link: ${data.link}`, { variant: "info" });
      }
      setInviteEmail("");
      setInviteRole("user");
      fetchInvitations(org._id);
    } catch (err) {
      showError("Failed to create invitation");
    }
  }

  async function handleCancelInvitationConfirmed() {
    if (!cancelInvId) return;
    try {
      await cancelInvitation(cancelInvId);
      showSuccess("Invitation canceled");
      if (org && org._id) fetchInvitations(org._id);
    } catch (err) {
      showError("Failed to cancel invitation");
    }
    closeCancelDialog();
  }

  function openCancelDialog(invId, invEmail) {
    setCancelInvId(invId);
    setCancelInvEmail(invEmail);
    setCancelDialogOpen(true);
  }

  function closeCancelDialog() {
    setCancelDialogOpen(false);
    setCancelInvId(null);
    setCancelInvEmail("");
  }

  function copyToClipboard(text) {
    navigator.clipboard.writeText(text).then(
      () => {
        showSuccess("Copied to clipboard");
      },
      () => {
        showError("Failed to copy");
      }
    );
  }

  async function handleApproveJoinRequest(requestId) {
    try {
      await approveJoinRequest(requestId);
      showSuccess("Join request approved");
      fetchOrganization(); // Refresh data
    } catch (err) {
      showError("Failed to approve join request");
    }
  }

  async function handleRejectJoinRequest(requestId) {
    try {
      await rejectJoinRequest(requestId);
      showSuccess("Join request rejected");
      if (org && org._id) fetchJoinRequests(org._id);
    } catch (err) {
      showError("Failed to reject join request");
    }
  }

  if (!org) {
    return (
      <Container sx={{ mt: 3 }}>
        {error && <p style={{ color: "red" }}>{error}</p>}
        {!error && <Typography>Loading org data...</Typography>}
      </Container>
    );
  }

  return (
    <Container sx={{ mt: 3 }}>
      <Typography variant="h5" gutterBottom>
        Organization Members
      </Typography>
      {error && <p style={{ color: "red" }}>{error}</p>}

      <Typography variant="subtitle1" gutterBottom>
        Org Name: {org.name}
      </Typography>

      {/* Organization ID Display */}
      <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
        <Typography variant="h6" gutterBottom>
          Organization Information
        </Typography>
        
        {/* Organization ID */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Organization ID
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body1" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
              {org._id}
            </Typography>
            <IconButton 
              size="small" 
              onClick={() => copyToClipboard(org._id)}
              title="Copy Organization ID"
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Share this ID with people so they can search and request to join your organization.
          </Typography>
        </Box>

        {/* Organization Invitation Link */}
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Organization Invitation Link
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography 
              variant="body2" 
              sx={{ 
                fontFamily: 'monospace', 
                backgroundColor: 'background.paper', 
                p: 1, 
                borderRadius: 0.5,
                border: 1,
                borderColor: 'divider',
                flex: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
            >
              {`${FRONT_URL}/join-org/${org._id}`}
            </Typography>
            <IconButton 
              size="small" 
              onClick={() => copyToClipboard(`${FRONT_URL}/join-org/${org._id}`)}
              title="Copy Invitation Link"
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Share this link with people you want to invite. They will register and automatically request to join your organization.
          </Typography>
        </Box>
      </Box>

      {/* MEMBERS TABLE */}
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>User</TableCell>
            <TableCell>Email</TableCell>
            <TableCell>Role</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {org.users?.map((entry) => {
            const member = entry.user;
            if (!member) return null;
            
            // Get user initials for avatar fallback
            const getInitials = (name) => {
              if (!name) return '?';
              const words = name.split(' ');
              if (words.length === 1) return name.charAt(0).toUpperCase();
              return (words[0].charAt(0) + words[words.length - 1].charAt(0)).toUpperCase();
            };

            // Generate consistent avatar color based on user ID
            const getAvatarColor = (userId, name) => {
              const colors = [
                '#FF6B6B', // Red
                '#4ECDC4', // Teal
                '#45B7D1', // Blue
                '#96CEB4', // Green
                '#FECA57', // Yellow
                '#FF9FF3', // Pink
                '#54A0FF', // Light Blue
                '#5F27CD', // Purple
                '#00D2D3', // Cyan
                '#FF9F43', // Orange
                '#10AC84', // Emerald
                '#EE5A24', // Dark Orange
                '#0984E3', // Ocean Blue
                '#6C5CE7', // Lavender
                '#FD79A8', // Rose
              ];
              
              // Use user ID or name to generate consistent color index
              const seed = userId || name || '';
              let hash = 0;
              for (let i = 0; i < seed.length; i++) {
                hash = seed.charCodeAt(i) + ((hash << 5) - hash);
              }
              const index = Math.abs(hash) % colors.length;
              return colors[index];
            };
            
            return (
              <TableRow key={member._id}>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar
                      src={member.avatar?.thumbnailUrl || member.avatar?.url}
                      alt={member.username}
                      sx={{ 
                        width: 40, 
                        height: 40,
                        bgcolor: !member.avatar?.url ? getAvatarColor(member._id, member.username) : undefined,
                        color: 'white',
                        fontWeight: 'bold'
                      }}
                    >
                      {getInitials(member.username)}
                    </Avatar>
                    <Typography>{member.username}</Typography>
                  </Box>
                </TableCell>
                <TableCell>{member.email}</TableCell>
                <TableCell>
                {member._id === user._id ? (
                    <Typography color="text.secondary">{entry.role}</Typography>
                  ) : (
                  <Select
                    value={entry.role}
                    onChange={(e) => handleRoleChange(member._id, e.target.value)}
                    sx={{ minWidth: 100 }}
                  >
                    <MenuItem value="user">User</MenuItem>
                    <MenuItem value="admin">Admin</MenuItem>
                  </Select>
                  )}
                </TableCell>
                <TableCell>
                  {member._id === user._id ? (
                    <Typography color="text.secondary">Cannot remove self</Typography>
                  ) : (
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={() => openRemoveDialog(member._id, member.username)}
                    >
                      Remove
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      {/* INVITE NEW MEMBER */}
      <Box sx={{ mt: 4 }}>
        <Typography variant="h6" gutterBottom>
          Invite New Member
        </Typography>
        <Box
          component="form"
          onSubmit={handleInvite}
          sx={{ display: "flex", gap: 2, maxWidth: 400 }}
        >
          <TextField
            label="Email"
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            required
          />
          <Select
            value={inviteRole}
            onChange={(e) => setInviteRole(e.target.value)}
          >
            <MenuItem value="user">User</MenuItem>
            <MenuItem value="admin">Admin</MenuItem>
          </Select>
          <Button type="submit" variant="contained">
            Invite
          </Button>
        </Box>
      </Box>

      {/* PENDING INVITATIONS */}
      <Box sx={{ mt: 4 }}>
        <Typography variant="h6" gutterBottom>
          Pending Invitations
        </Typography>
        {invitations.length === 0 ? (
          <Typography>No pending invitations</Typography>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Email</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Invitation Link</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {invitations.map((inv) => {
                // Example link
                const linkUrl = `${FRONT_URL}/invite/${inv.token}`;
                return (
                  <TableRow key={inv._id}>
                    <TableCell>{inv.email}</TableCell>
                    <TableCell>{inv.role}</TableCell>
                    <TableCell> 
                        {inv.token.slice(0, 20)}...
                      <IconButton
                        size="small"
                        onClick={() => copyToClipboard(linkUrl)}
                      >
                        <ContentCopyIcon fontSize="inherit" />
                      </IconButton>
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={() => openCancelDialog(inv._id, inv.email)}
                      >
                        Cancel
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Box>

      {/* JOIN REQUESTS */}
      <Box sx={{ mt: 4 }}>
        <Typography variant="h6" gutterBottom>
          Join Requests
        </Typography>
        {joinRequests.length === 0 ? (
          <Typography>No pending join requests</Typography>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>User</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {joinRequests.map((request) => {
                const requestUser = request.userId;
                
                // Get user initials for avatar fallback
                const getInitials = (name) => {
                  if (!name) return '?';
                  const words = name.split(' ');
                  if (words.length === 1) return name.charAt(0).toUpperCase();
                  return (words[0].charAt(0) + words[words.length - 1].charAt(0)).toUpperCase();
                };

                // Generate consistent avatar color based on user ID
                const getAvatarColor = (userId, name) => {
                  const colors = [
                    '#FF6B6B', // Red
                    '#4ECDC4', // Teal
                    '#45B7D1', // Blue
                    '#96CEB4', // Green
                    '#FECA57', // Yellow
                    '#FF9FF3', // Pink
                    '#54A0FF', // Light Blue
                    '#5F27CD', // Purple
                    '#00D2D3', // Cyan
                    '#FF9F43', // Orange
                    '#10AC84', // Emerald
                    '#EE5A24', // Dark Orange
                    '#0984E3', // Ocean Blue
                    '#6C5CE7', // Lavender
                    '#FD79A8', // Rose
                  ];
                  
                  // Use user ID or name to generate consistent color index
                  const seed = userId || name || '';
                  let hash = 0;
                  for (let i = 0; i < seed.length; i++) {
                    hash = seed.charCodeAt(i) + ((hash << 5) - hash);
                  }
                  const index = Math.abs(hash) % colors.length;
                  return colors[index];
                };
                
                return (
                  <TableRow key={request._id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar
                          src={requestUser?.avatar?.thumbnailUrl || requestUser?.avatar?.url}
                          alt={requestUser?.username || 'Unknown'}
                          sx={{ 
                            width: 40, 
                            height: 40,
                            bgcolor: !requestUser?.avatar?.url ? getAvatarColor(requestUser?._id, requestUser?.username) : undefined,
                            color: 'white',
                            fontWeight: 'bold'
                          }}
                        >
                          {getInitials(requestUser?.username || 'Unknown')}
                        </Avatar>
                        <Typography>{requestUser?.username || 'Unknown'}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{requestUser?.email || 'Unknown'}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          variant="contained"
                          color="success"
                          size="small"
                          onClick={() => handleApproveJoinRequest(request._id)}
                        >
                          Approve
                        </Button>
                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          onClick={() => handleRejectJoinRequest(request._id)}
                        >
                          Reject
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Box>

      {/* REMOVE USER DIALOG */}
      <Dialog open={removeDialogOpen} onClose={closeRemoveDialog}>
        <DialogTitle>Confirm User Removal</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to remove user "{removeUserName}" from the org?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeRemoveDialog}>Cancel</Button>
          <Button onClick={handleRemoveConfirmed} color="error" variant="contained">
            Remove
          </Button>
        </DialogActions>
      </Dialog>

      {/* CANCEL INVITATION DIALOG */}
      <Dialog open={cancelDialogOpen} onClose={closeCancelDialog}>
        <DialogTitle>Confirm Invitation Cancellation</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to cancel the invitation for "{cancelInvEmail}"?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCancelDialog}>Back</Button>
          <Button onClick={handleCancelInvitationConfirmed} color="error" variant="contained">
            Cancel Invitation
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default OrganizationMembersPage;
